import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { qualityStandards } from '../data/mock';
import { Award, ClipboardCheck, Shield, Gem, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';

const iconMap = {
  Award: Award,
  ClipboardCheck: ClipboardCheck,
  Shield: Shield,
  Gem: Gem
};

export const QualityPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page Header */}
      <section className="relative pt-32 pb-16 bg-gradient-to-br from-slate-50 via-orange-50/30 to-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-orange-100 rounded-full mb-4">
              <Award size={40} className="text-[#D8461D]" />
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold text-slate-900">
              Quality Standards
            </h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Commitment to excellence at every stage of manufacturing
            </p>
          </div>
        </div>
      </section>

      {/* Quality Pillars */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {qualityStandards.map((standard) => {
              const IconComponent = iconMap[standard.icon];
              return (
                <Card
                  key={standard.id}
                  className="group hover:shadow-xl transition-all duration-300 border-slate-200 hover:border-orange-300 text-center bg-white"
                >
                  <CardContent className="p-8 space-y-4">
                    <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto group-hover:bg-[#D8461D] transition-colors duration-300">
                      <IconComponent size={36} className="text-[#D8461D] group-hover:text-white transition-colors duration-300" />
                    </div>
                    <h3 className="text-xl font-bold text-slate-900 group-hover:text-[#D8461D] transition-colors">
                      {standard.title}
                    </h3>
                    <p className="text-sm text-slate-600 leading-relaxed">
                      {standard.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quality Process */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Our Quality Process
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              A comprehensive approach to ensuring product excellence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                step: '01',
                title: 'Material Selection',
                description: 'Premium raw materials sourced from trusted suppliers worldwide'
              },
              {
                step: '02',
                title: 'Manufacturing',
                description: 'State-of-the-art production facility with modern equipment'
              },
              {
                step: '03',
                title: 'Quality Testing',
                description: 'Rigorous testing protocols at every stage of production'
              },
              {
                step: '04',
                title: 'Certification',
                description: 'Products meet or exceed international quality standards'
              }
            ].map((process, index) => (
              <Card key={index} className="border-slate-200 bg-white hover:shadow-lg transition-shadow">
                <CardContent className="p-6 space-y-3">
                  <div className="text-4xl font-bold text-[#D8461D]">{process.step}</div>
                  <h3 className="text-xl font-bold text-slate-900">{process.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{process.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-orange-50 to-slate-50 rounded-2xl p-8 md:p-12 border border-orange-100">
            <div className="max-w-3xl mx-auto text-center space-y-6">
              <h3 className="text-3xl font-bold text-slate-900">
                Certified Excellence
              </h3>
              <p className="text-lg text-slate-600 leading-relaxed">
                Our manufacturing facility follows strict quality control protocols. Every product undergoes 
                comprehensive testing to ensure it meets international standards and exceeds customer expectations. 
                We are committed to delivering reliable, durable solutions for your industrial needs.
              </p>
              <div className="flex flex-wrap justify-center gap-4 pt-4">
                <div className="px-6 py-3 bg-white rounded-lg shadow-sm border border-slate-200">
                  <span className="font-bold text-[#D8461D]">ISO 9001</span>
                </div>
                <div className="px-6 py-3 bg-white rounded-lg shadow-sm border border-slate-200">
                  <span className="font-bold text-[#D8461D]">Quality Tested</span>
                </div>
                <div className="px-6 py-3 bg-white rounded-lg shadow-sm border border-slate-200">
                  <span className="font-bold text-[#D8461D]">CE Certified</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Commitment */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-8 text-center">
              Our Quality Commitment
            </h2>
            <div className="space-y-4">
              {[
                'Every product is manufactured under strict quality control procedures',
                'Regular audits and inspections ensure compliance with international standards',
                'Advanced testing equipment validates product performance and durability',
                'Continuous improvement initiatives drive innovation and excellence',
                'Customer feedback is integrated into our quality management system',
                'Zero-defect manufacturing philosophy guides our production processes'
              ].map((commitment, index) => (
                <div key={index} className="flex items-start gap-4 bg-slate-50 p-4 rounded-lg">
                  <CheckCircle2 size={24} className="text-[#D8461D] flex-shrink-0 mt-0.5" />
                  <p className="text-slate-700 text-lg">{commitment}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#D8461D]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h3 className="text-3xl lg:text-4xl font-bold text-white">
            Experience Quality You Can Trust
          </h3>
          <p className="text-xl text-orange-100">
            Discover our range of ISO-certified industrial hoses and pipes
          </p>
          <Link to="/products">
            <Button size="lg" className="bg-white text-[#D8461D] hover:bg-slate-100 px-8 py-6 text-lg font-semibold">
              View Products
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default QualityPage;
